import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { specialArticles, specialGroups } from "@/data/specials";
import { filmPicks } from "@/data/films";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Special() {
  usePageMeta({
    title: "色戒电影深度解读专题 - 影评与幕后合辑",
    description:
      "色戒电影深度解读专题，从影评、幕后与角色三个方向整理六篇长文，聊戒指的隐喻、牌桌上的权力流转、旗袍与雨夜的时代质感，以及那个改变一切的选择。",
    keywords: "色戒深度解读,色戒影评,色戒幕后故事,色戒角色分析,色戒专题",
  });

  const featured = specialArticles[0];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="relative overflow-hidden border-b border-background-200 pb-12 pt-28 md:pb-16 md:pt-40">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20dark%20library%20with%20tall%20wooden%20bookshelves%2C%20a%20single%20reading%20lamp%20casting%20warm%20amber%20light%2C%20deep%20wine%20red%20armchair%2C%20haze%20and%20dust%20in%20the%20air%2C%20moody%20atmospheric%20wide%20composition%2C%20no%20text&width=1920&height=900&seq=sj-special-hero&orientation=landscape"
              alt="专题合辑页背景"
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-background-50/88" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30" />
          </div>

          <div className="container-page relative">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "专题" }]} />
            <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1 font-label text-[11px] tracking-[0.2em] text-accent-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                  深度专题合辑
                </span>
                <h1 className="mt-5 font-heading text-3xl leading-tight text-foreground-950 md:text-[44px]">
                  色戒电影<span className="text-accent-500">深度解读</span>
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                  六个角度，六篇长文。我们把镜头里容易被忽略的细节重新摆出来——一枚戒指、一场牌局、一件旗袍，以及那个让所有事情失控的三分钟。
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {specialGroups.map((group) => (
                  <a
                    key={group}
                    href={`#group-${group}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-950/20 px-4 py-2.5 font-label text-xs text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    {group}
                    <span className="text-foreground-500">
                      {specialArticles.filter((item) => item.group === group).length}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="container-page py-12 md:py-16">
          <Link
            to={`/special/${featured.slug}`}
            className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-accent-500/60 lg:grid-cols-[1.1fr_1fr]"
          >
            <div className="relative aspect-[16/10] w-full overflow-hidden lg:aspect-auto lg:h-full lg:min-h-[340px]">
              <img
                src={featured.cover}
                alt={`${featured.title} 专题封面`}
                title={`${featured.title} 色戒深度解读`}
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50/70 via-transparent to-transparent lg:bg-gradient-to-r" />
              <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 font-label text-[11px] font-medium text-background-50">
                编辑精选
              </span>
            </div>
            <div className="flex flex-col justify-center p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-2 font-label text-[11px] text-foreground-500">
                <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-secondary-900">{featured.group}</span>
                <span>{featured.date}</span>
                <span>· {featured.readTime}</span>
              </div>
              <h2 className="mt-4 font-heading text-2xl leading-snug text-foreground-950 transition-colors group-hover:text-accent-400 md:text-[30px]">
                {featured.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{featured.subtitle}</p>
              <p className="mt-4 line-clamp-3 text-xs leading-relaxed text-foreground-600">
                {featured.paragraphs[0]}
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 font-label text-sm text-accent-500">
                阅读全文
                <i className="ri-arrow-right-line text-sm leading-none transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        </section>

        {specialGroups.map((group) => {
          const items = specialArticles.filter((article) => article.group === group);
          return (
            <section key={group} id={`group-${group}`} className="container-page scroll-mt-24 pb-12 md:pb-16">
              <div className="flex items-end justify-between gap-4 border-b border-background-200 pb-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md border border-accent-500/30 bg-accent-500/10 text-accent-400">
                    <i
                      className={`${
                        group === "影评"
                          ? "ri-quill-pen-line"
                          : group === "幕后"
                            ? "ri-clapperboard-line"
                            : "ri-user-heart-line"
                      } text-base leading-none`}
                    />
                  </span>
                  <div>
                    <h2 className="font-heading text-lg text-foreground-950 md:text-xl">{group}</h2>
                    <p className="font-label text-[11px] text-foreground-500">{items.length} 篇长文</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                {items.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/special/${article.slug}`}
                    className="group surface-card flex flex-col overflow-hidden transition-colors hover:border-accent-500/60 sm:flex-row"
                  >
                    <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden sm:aspect-auto sm:h-auto sm:w-40">
                      <img
                        src={article.cover}
                        alt={`${article.title} 文章配图`}
                        title={`${article.title} ${article.group}`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1 p-4 md:p-5">
                      <div className="flex flex-wrap items-center gap-2 font-label text-[11px] text-foreground-500">
                        <span>{article.readTime}阅读</span>
                        <span>·</span>
                        <span>{article.date}</span>
                      </div>
                      <h3 className="mt-2 font-heading text-base text-foreground-950 transition-colors group-hover:text-accent-400">
                        {article.title}
                      </h3>
                      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                        {article.subtitle}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 font-label text-xs text-accent-500">
                        查看正文
                        <i className="ri-arrow-right-line text-xs leading-none" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        <section id="films" className="container-page scroll-mt-24 pb-16 md:pb-20">
          <div className="flex flex-col gap-3 border-b border-background-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-px w-8 bg-accent-500" />
                <span className="font-label text-xs tracking-[0.28em] text-accent-500">延伸观影</span>
              </div>
              <h2 className="mt-3 font-heading text-xl text-foreground-950 md:text-2xl">
                同类型推荐 · 看完还能接着看
              </h2>
            </div>
            <p className="max-w-md text-xs leading-relaxed text-foreground-500">
              以下为编辑推荐清单，仅作观影方向参考，不提供片源。
            </p>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filmPicks.map((film) => (
              <div
                key={film.id}
                className="surface-card flex items-start gap-4 p-4 transition-colors hover:border-accent-500/50"
              >
                <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md border border-background-200">
                  <img
                    src={film.cover}
                    alt={`${film.title} 推荐封面`}
                    title={`${film.title} 影视推荐`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="truncate font-heading text-sm text-foreground-950">{film.title}</h3>
                    <span className="shrink-0 font-label text-[11px] text-accent-400">{film.rating}</span>
                  </div>
                  <p className="mt-1 font-label text-[11px] text-foreground-500">
                    {film.year} · {film.genre}
                  </p>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{film.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="container-page pb-16 md:pb-24">
          <div className="flex flex-col items-start justify-between gap-5 rounded-lg border border-background-200 bg-background-100 p-6 md:flex-row md:items-center md:p-8">
            <div>
              <p className="font-label text-[11px] tracking-[0.2em] text-accent-500">关联入口</p>
              <h2 className="mt-2 font-heading text-xl text-foreground-950 md:text-2xl">
                读完解析，再从第一章重新看一遍
              </h2>
              <p className="mt-2 max-w-xl text-sm text-foreground-600">
                按剧情时间线整理的 12 个章节，支持关键词搜索、时间线筛选与本地收藏。
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/category"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-list-check text-base leading-none" />
                章节导航
              </Link>
              <Link
                to="/detail/chapter-01"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                <i className="ri-play-circle-line text-base leading-none" />
                从第一章开始
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}